import React, { useEffect, useState, useMemo } from 'react';

interface PricePoint {
  site: string;
  url?: string;
  product_name?: string;
  prices: { quantity: number | null; total: number; per_pill: number | null }[];
}

interface ComparisonRow {
  rank: number;
  name: string;
  category: string;
  ingredient: string;
  ourPerPill: number | null;
  ourQty: number | null;
  ourStrength: string | null;
  compBest: number | null;
  compSite: string | null;
  compCount: number;
  compPriceCount: number;
  diff: number | null;
  diffPct: number | null;
  competitors: PricePoint[];
}

const CATEGORY_LABELS: Record<string, string> = {
  'weight-management': 'Weight Management',
  'diabetes': 'Diabetes',
  'hair-loss': 'Hair Loss',
  'skin-care': 'Skin Care',
  'beauty-skincare': 'Beauty & Skin',
  'hormones': 'Hormones',
  'heart-health': 'Heart / BP',
  'antibiotics': 'Antibiotics',
  'antiparasitic': 'Antiparasitic',
  'respiratory': 'Respiratory',
  'sexual-health': 'Sexual Health (ED)',
};

const AdminPriceReview: React.FC = () => {
  const [rows, setRows] = useState<ComparisonRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCat, setSelectedCat] = useState<string>('weight-management');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('diff-desc');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetch('/price_comparison.json')
      .then(r => r.json())
      .then((data: ComparisonRow[]) => {
        setRows(data);
        setLoading(false);
      })
      .catch(e => {
        console.error('Failed to load price comparison data:', e);
        setLoading(false);
      });
  }, []);

  // Exclude sexual-health (already priced)
  const categories = useMemo(() => {
    const seen: Record<string, boolean> = {};
    const cats: string[] = [];
    rows.forEach(r => {
      if (!seen[r.category] && r.category !== 'sexual-health') {
        seen[r.category] = true;
        cats.push(r.category);
      }
    });
    return cats.sort();
  }, [rows]);

  const filtered = useMemo(() => {
    let list = rows.filter(r => r.category === selectedCat);

    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r => (r.name + ' ' + r.ingredient).toLowerCase().includes(q));
    }

    if (statusFilter) {
      list = list.filter(r => {
        const status = !r.compBest ? 'nodata' : r.diff! < -0.001 ? 'cheaper' : Math.abs(r.diffPct!) <= 10 ? 'similar' : 'expensive';
        if (statusFilter === 'expensive-50') return status === 'expensive' && r.diffPct! > 50;
        if (statusFilter === 'expensive-100') return status === 'expensive' && r.diffPct! > 100;
        return status === statusFilter;
      });
    }

    if (sortBy === 'rank') list.sort((a, b) => a.rank - b.rank);
    else if (sortBy === 'name') list.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortBy === 'our-low') list.sort((a, b) => (a.ourPerPill || 999) - (b.ourPerPill || 999));
    else if (sortBy === 'our-high') list.sort((a, b) => (b.ourPerPill || 0) - (a.ourPerPill || 0));
    else if (sortBy === 'diff-desc') list.sort((a, b) => (b.diffPct ?? -999) - (a.diffPct ?? -999));
    else if (sortBy === 'diff-asc') list.sort((a, b) => (a.diffPct ?? 999) - (b.diffPct ?? 999));

    return list;
  }, [rows, selectedCat, search, statusFilter, sortBy]);

  const stats = useMemo(() => {
    const list = rows.filter(r => r.category === selectedCat);
    const both = list.filter(r => r.ourPerPill && r.compBest);
    return {
      total: list.length,
      comparable: both.length,
      cheaper: list.filter(r => r.diff !== null && r.diff < -0.001).length,
      similar: both.filter(r => Math.abs(r.diffPct!) <= 10).length,
      expensive: list.filter(r => r.diff !== null && r.diff > 0.001).length,
      nodata: list.filter(r => !r.compBest).length,
    };
  }, [rows, selectedCat]);

  const toggleExpand = (rank: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(rank)) next.delete(rank); else next.add(rank);
      return next;
    });
  };

  if (loading) {
    return <div className="text-warm-500 text-[13px]">Loading price comparison data...</div>;
  }

  return (
    <div className="space-y-5">
      {/* Category tabs */}
      <div className="flex flex-wrap gap-2">
        {categories.map(cat => {
          const count = rows.filter(r => r.category === cat).length;
          const active = cat === selectedCat;
          return (
            <button
              key={cat}
              onClick={() => { setSelectedCat(cat); setExpanded(new Set()); }}
              className={`px-3 py-1.5 text-[12px] font-medium rounded-lg transition-colors ${
                active
                  ? 'bg-forest-800 text-cream-50'
                  : 'bg-white/80 border border-warm-200 text-warm-600 hover:text-forest-800 hover:border-forest-200'
              }`}
            >
              {CATEGORY_LABELS[cat] || cat} <span className="opacity-60 ml-1">({count})</span>
            </button>
          );
        })}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-white/80 border border-warm-200/60 rounded-xl px-4 py-3">
          <div className="text-[9px] font-semibold text-warm-400 uppercase tracking-[0.1em]">Total</div>
          <div className="text-[20px] font-bold text-forest-900">{stats.total}</div>
        </div>
        <div className="bg-white/80 border border-warm-200/60 rounded-xl px-4 py-3">
          <div className="text-[9px] font-semibold text-warm-400 uppercase tracking-[0.1em]">Comparable</div>
          <div className="text-[20px] font-bold text-forest-900">{stats.comparable}</div>
        </div>
        <div className="bg-white/80 border border-emerald-200/60 rounded-xl px-4 py-3">
          <div className="text-[9px] font-semibold text-emerald-600 uppercase tracking-[0.1em]">Cheaper</div>
          <div className="text-[20px] font-bold text-emerald-700">{stats.cheaper}</div>
        </div>
        <div className="bg-white/80 border border-blue-200/60 rounded-xl px-4 py-3">
          <div className="text-[9px] font-semibold text-blue-600 uppercase tracking-[0.1em]">Similar</div>
          <div className="text-[20px] font-bold text-blue-700">{stats.similar}</div>
        </div>
        <div className="bg-white/80 border border-rose-200/60 rounded-xl px-4 py-3">
          <div className="text-[9px] font-semibold text-rose-600 uppercase tracking-[0.1em]">Expensive</div>
          <div className="text-[20px] font-bold text-rose-700">{stats.expensive}</div>
        </div>
        <div className="bg-white/80 border border-warm-200/60 rounded-xl px-4 py-3">
          <div className="text-[9px] font-semibold text-warm-400 uppercase tracking-[0.1em]">No Data</div>
          <div className="text-[20px] font-bold text-warm-500">{stats.nodata}</div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-2 items-center">
        <input
          type="text"
          placeholder="Search product or ingredient..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 min-w-[200px] px-3 py-2 text-[12.5px] border border-warm-200 rounded-lg bg-white focus:outline-none focus:border-forest-400"
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-[12.5px] border border-warm-200 rounded-lg bg-white focus:outline-none focus:border-forest-400"
        >
          <option value="">All Status</option>
          <option value="cheaper">We&apos;re Cheaper</option>
          <option value="similar">Similar (±10%)</option>
          <option value="expensive">More Expensive</option>
          <option value="expensive-50">Expensive +50%</option>
          <option value="expensive-100">Expensive +100%</option>
          <option value="nodata">No Competitor Data</option>
        </select>
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          className="px-3 py-2 text-[12.5px] border border-warm-200 rounded-lg bg-white focus:outline-none focus:border-forest-400"
        >
          <option value="diff-desc">Most Expensive First</option>
          <option value="diff-asc">Cheapest First</option>
          <option value="rank">Priority</option>
          <option value="name">Name A-Z</option>
          <option value="our-low">Our Price Low</option>
          <option value="our-high">Our Price High</option>
        </select>
        <span className="text-[11px] text-warm-400">Showing {filtered.length}</span>
      </div>

      {/* Table */}
      <div className="bg-white/80 border border-warm-200/60 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="bg-cream-100/50 border-b border-warm-200/60">
                <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-warm-500 uppercase tracking-[0.08em]">Product</th>
                <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-warm-500 uppercase tracking-[0.08em]">Our $/pill</th>
                <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-warm-500 uppercase tracking-[0.08em]">Best Competitor</th>
                <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-warm-500 uppercase tracking-[0.08em]">Diff</th>
                <th className="text-center px-3 py-2.5 text-[10px] font-semibold text-warm-500 uppercase tracking-[0.08em]">Status</th>
                <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-warm-500 uppercase tracking-[0.08em]">Data</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const status = !r.compBest ? 'nodata' : r.diff! < -0.001 ? 'cheaper' : Math.abs(r.diffPct!) <= 10 ? 'similar' : 'expensive';
                const isOpen = expanded.has(r.rank);
                const statusBadge =
                  status === 'cheaper' ? <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Cheaper</span>
                  : status === 'similar' ? <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200">Similar</span>
                  : status === 'expensive' ? <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full bg-rose-50 text-rose-700 border border-rose-200">+{Math.round(r.diffPct || 0)}%</span>
                  : <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full bg-warm-50 text-warm-500 border border-warm-200">No data</span>;

                return (
                  <React.Fragment key={r.rank}>
                    <tr
                      onClick={() => toggleExpand(r.rank)}
                      className="border-b border-warm-100 hover:bg-cream-50 cursor-pointer"
                    >
                      <td className="px-3 py-2.5 max-w-[300px]">
                        <div className="font-semibold text-forest-900 truncate">{r.name}</div>
                        {r.ingredient && (
                          <div className="text-[10.5px] text-warm-400 truncate">{r.ingredient}</div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                        {r.ourPerPill ? (
                          <>
                            <div className="font-semibold text-forest-800">${r.ourPerPill.toFixed(2)}</div>
                            <div className="text-[10px] text-warm-400">{r.ourStrength} / {r.ourQty}ct</div>
                          </>
                        ) : <span className="text-warm-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                        {r.compBest ? (
                          <>
                            <div className="font-semibold text-indigo-700">${r.compBest.toFixed(2)}</div>
                            <div className="text-[10px] text-warm-400">{r.compSite}</div>
                          </>
                        ) : <span className="text-warm-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                        {r.diff !== null ? (
                          <span className={r.diff < 0 ? 'text-emerald-700 font-semibold' : r.diff > 0 ? 'text-rose-700 font-semibold' : 'text-warm-500'}>
                            {r.diff > 0 ? '+' : ''}{r.diffPct?.toFixed(0)}%
                          </span>
                        ) : <span className="text-warm-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-center">{statusBadge}</td>
                      <td className="px-3 py-2.5 text-right text-[10.5px] text-warm-400">
                        {r.compCount} / {r.compPriceCount}
                      </td>
                    </tr>
                    {isOpen && r.competitors && r.competitors.length > 0 && (
                      <tr className="bg-cream-50/80 border-b border-warm-100">
                        <td colSpan={6} className="px-3 py-3">
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {r.competitors.filter(c => c.prices?.some(p => p.per_pill)).map((c, i) => (
                              <div key={i} className="bg-white border border-warm-200/60 rounded-lg p-2.5">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-semibold text-forest-700 text-[11.5px]">{c.site}</span>
                                  {c.url && (
                                    <a href={c.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-[10px] text-forest-500 hover:text-forest-800">
                                      visit →
                                    </a>
                                  )}
                                </div>
                                {c.product_name && (
                                  <div className="text-[10px] text-warm-400 mb-1 line-clamp-1">{c.product_name}</div>
                                )}
                                <div className="text-[11px] text-warm-600 space-y-0.5">
                                  {c.prices.filter(p => p.per_pill).map((p, j) => (
                                    <div key={j} className="flex justify-between">
                                      <span>{p.quantity || '?'}ct</span>
                                      <span className="font-mono font-semibold text-forest-800">${p.per_pill?.toFixed(2)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-warm-400 text-[12px]">
                    No products match the filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminPriceReview;
